import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  AlertTriangle, Copy, Mail, Globe, X, Lock,
  Eye, EyeOff, CheckCircle2, KeyRound, Shield, ShieldCheck, Wallet
} from 'lucide-react';
import TopUpModalLite from '@etherworks/billing-ui/TopUpModalLite';
import { useAuth } from '../../contexts/AuthContext';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import PageTitle from '../../components/PageTitle';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/errorMessages';

interface TwoFactorStatus {
  enabled: boolean;
  hasBackupCodes: boolean;
}

interface SetupPayload {
  secret: string;
  qrCode: string;
  otpauth: string;
}

export default function Account() {
  const { t } = useTranslation(['settings', 'common']);
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();

  // Top-up modal
  const [topUpOpen, setTopUpOpen] = useState(false);

  // Password change
  const [passwordModal, setPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Two-factor authentication
  const [twoFactorStatus, setTwoFactorStatus] = useState<TwoFactorStatus | null>(null);
  const [twoFactorLoading, setTwoFactorLoading] = useState(true);
  const [twoFactorSetupData, setTwoFactorSetupData] = useState<SetupPayload | null>(null);
  const [twoFactorVerificationCode, setTwoFactorVerificationCode] = useState('');
  const [twoFactorPassword, setTwoFactorPassword] = useState('');
  const [twoFactorDisableCode, setTwoFactorDisableCode] = useState('');
  const [twoFactorBackupCodes, setTwoFactorBackupCodes] = useState<string[]>([]);
  const [twoFactorSetupOpen, setTwoFactorSetupOpen] = useState(false);
  const [twoFactorDisableOpen, setTwoFactorDisableOpen] = useState(false);
  const [twoFactorBackupCodesOpen, setTwoFactorBackupCodesOpen] = useState(false);
  const [twoFactorSetupLoading, setTwoFactorSetupLoading] = useState(false);
  const [twoFactorEnableLoading, setTwoFactorEnableLoading] = useState(false);
  const [twoFactorDisableLoading, setTwoFactorDisableLoading] = useState(false);

  const passwordRequirements = [
    { label: t('account.min_characters'), check: (p: string) => p.length >= 8 },
    { label: t('account.char_variety'), check: (p: string) => {
      let score = 0;
      if (/[a-z]/.test(p)) score++;
      if (/[A-Z]/.test(p)) score++;
      if (/[0-9]/.test(p)) score++;
      if (/[^A-Za-z0-9]/.test(p)) score++;
      return score >= 3;
    }},
  ];

  const allRequirementsMet = passwordRequirements.every(r => r.check(newPassword));
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;
  const canSubmitPassword = currentPassword && allRequirementsMet && passwordsMatch && !passwordLoading;

  const loadTwoFactorStatus = async () => {
    setTwoFactorLoading(true);
    try {
      const response = await axios.get('/api/auth/2fa/status');
      if (response.data?.code === 0 && response.data?.data) {
        setTwoFactorStatus(response.data.data);
        return;
      }
      toast.error(getApiErrorMessage(response.data, t, 'security.failedToLoad'));
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, t, 'security.failedToLoad'));
    } finally {
      setTwoFactorLoading(false);
    }
  };

  useEffect(() => {
    void loadTwoFactorStatus();
  }, []);

  const resetPasswordForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handleChangePassword = async () => {
    if (!canSubmitPassword) return;
    setPasswordLoading(true);
    try {
      const res = await axios.post('/api/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      if (res.data.code === 0) {
        toast.success(t('account.toast_password_success'));
        setPasswordModal(false);
        resetPasswordForm();
        setTimeout(() => {
          logout();
          navigate('/auth?mode=login');
        }, 1500);
      } else {
        toast.error(getApiErrorMessage(res.data, t, 'account.toast_password_fail'));
      }
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, t, 'account.toast_password_fail'));
    } finally {
      setPasswordLoading(false);
    }
  };

  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common:copied_to_clipboard'));
    } catch {
      toast.error(t('common:failed_to_copy'));
    }
  };

  const handleOpenTwoFactorSetup = async () => {
    setTwoFactorSetupOpen(true);
    setTwoFactorVerificationCode('');
    if (twoFactorSetupData) return;

    setTwoFactorSetupLoading(true);
    try {
      const response = await axios.post('/api/auth/2fa/setup');
      if (response.data?.code === 0 && response.data?.data) {
        setTwoFactorSetupData(response.data.data);
        return;
      }
      setTwoFactorSetupOpen(false);
      toast.error(getApiErrorMessage(response.data, t, 'security.failedToGenerate2FA'));
    } catch (error: any) {
      setTwoFactorSetupOpen(false);
      toast.error(getApiErrorMessage(error, t, 'security.failedToGenerate2FA'));
    } finally {
      setTwoFactorSetupLoading(false);
    }
  };

  const handleEnableTwoFactor = async () => {
    if (!twoFactorVerificationCode.trim()) {
      toast.error(t('security.enterVerificationCodeDesc'));
      return;
    }

    setTwoFactorEnableLoading(true);
    try {
      const response = await axios.post('/api/auth/2fa/enable', { code: twoFactorVerificationCode.trim() });
      if (response.data?.code === 0 && response.data?.data) {
        setTwoFactorStatus({ enabled: true, hasBackupCodes: true });
        setTwoFactorBackupCodes(response.data.data.backupCodes || []);
        setTwoFactorSetupOpen(false);
        setTwoFactorBackupCodesOpen(true);
        toast.success(t('security.twoFAEnabled'));
        return;
      }
      toast.error(getApiErrorMessage(response.data, t, 'security.failedToEnable2FA'));
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, t, 'security.failedToEnable2FA'));
    } finally {
      setTwoFactorEnableLoading(false);
    }
  };

  const handleDisableTwoFactor = async () => {
    if (!twoFactorPassword.trim()) {
      toast.error(t('security.enterYourPassword'));
      return;
    }
    if (!twoFactorDisableCode.trim()) {
      toast.error(t('security.enterVerificationCodeDesc'));
      return;
    }

    setTwoFactorDisableLoading(true);
    try {
      const response = await axios.post('/api/auth/2fa/disable', {
        password: twoFactorPassword.trim(),
        code: twoFactorDisableCode.trim(),
      });
      if (response.data?.code === 0) {
        setTwoFactorStatus({ enabled: false, hasBackupCodes: false });
        setTwoFactorDisableOpen(false);
        setTwoFactorBackupCodesOpen(false);
        setTwoFactorSetupData(null);
        setTwoFactorBackupCodes([]);
        setTwoFactorPassword('');
        setTwoFactorDisableCode('');
        toast.success(t('security.twoFADisabled'));
        return;
      }
      toast.error(getApiErrorMessage(response.data, t, 'security.failedToDisable2FA'));
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, t, 'security.failedToDisable2FA'));
    } finally {
      setTwoFactorDisableLoading(false);
    }
  };

  if (!user) return null;

  return (
    <>
      <div className="h-full w-full flex flex-col gap-4 max-w-full mx-auto">
        <PageTitle
          titleKey="account.title"
          namespace="settings"
          descriptionKey="account.subtitle"
        />
        {/* Section Header */}
        <div className="shrink-0">
          <h2 className="text-lg font-semibold text-foreground tracking-tight">{t('account.title')}</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t('account.subtitle')}
          </p>
        </div>

        <div className="space-y-4">
          {/* Balance Card */}
          <div className="bg-card rounded-lg border border-border shadow-xs p-5 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Wallet className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">{t('account.balance_title')}</h3>
                <p className="text-xs text-muted-foreground">{t('account.balance_desc')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="px-4 py-2 text-sm text-foreground font-semibold bg-background rounded-lg border border-border tabular-nums">
                ${(user.balance ?? 0).toFixed(2)} <span className="text-xs font-normal text-muted-foreground ml-1">{t('account.balance_currency')}</span>
              </div>
              <button
                onClick={() => setTopUpOpen(true)}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
				{t('account.balance_topup')}
              </button>
            </div>
          </div>

          {/* Password Card */}
          <div className="bg-card rounded-lg border border-border shadow-xs p-5 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Lock className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">{t('account.password_title')}</h3>
                <p className="text-xs text-muted-foreground">{t('account.password_desc')}</p>
              </div>
            </div>
            <button
              onClick={() => setPasswordModal(true)}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              {t('account.change_password')}
            </button>
          </div>

          {/* Email Card */}
          <div className="bg-card rounded-lg border border-border shadow-xs p-5 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Mail className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">{t('account.email_title')}</h3>
                <p className="text-xs text-muted-foreground">{t('account.email_desc')}</p>
              </div>
            </div>
            <div className="px-4 py-2 text-sm text-foreground font-medium bg-background rounded-lg border border-border">
              {user.email}
            </div>
          </div>

          {/* Language Card */}
          <div className="bg-card rounded-lg border border-border shadow-xs p-5 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Globe className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">{t('account.language_title')}</h3>
                <p className="text-xs text-muted-foreground">{t('account.language_desc')}</p>
              </div>
            </div>
            <LanguageSwitcher variant="full" />
          </div>

          <div className="bg-card rounded-lg border border-border shadow-xs p-5">
            {twoFactorLoading ? (
              <div className="space-y-3 animate-pulse">
                <div className="h-6 w-48 rounded bg-muted" />
                <div className="h-4 w-full rounded bg-muted" />
                <div className="h-4 w-3/4 rounded bg-muted" />
              </div>
            ) : (
              <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                <div className="flex items-start gap-4 min-w-0">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${twoFactorStatus?.enabled ? 'bg-green-500/10' : 'bg-primary/10'}`}>
                    {twoFactorStatus?.enabled ? (
                      <ShieldCheck className="h-5 w-5 text-green-500" />
                    ) : (
                      <Shield className="h-5 w-5 text-primary" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-semibold text-foreground">{t('security.twoFactorAuth')}</h3>
                      <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${twoFactorStatus?.enabled ? 'bg-green-500/10 text-green-600' : 'bg-muted text-muted-foreground'}`}>
                        {twoFactorStatus?.enabled ? t('security.enabled') : t('security.disabled')}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t('security.twoFactorAuthDesc')}
                    </p>
                    {twoFactorStatus?.enabled && twoFactorStatus.hasBackupCodes && (
                      <div className="mt-3 inline-flex items-center gap-2 rounded-lg bg-green-500/5 px-3 py-2 text-xs text-green-700 border border-green-500/10">
                        <CheckCircle2 className="h-4 w-4" />
                        <span>{t('security.backupCodesAvailable')}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3 xl:shrink-0">
                  {twoFactorStatus?.enabled ? (
                    <button
                      onClick={() => setTwoFactorDisableOpen(true)}
                      className="rounded-lg border border-destructive/30 px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/5 transition-colors"
                    >
                      {t('security.disable2FAButton')}
                    </button>
                  ) : (
                    <button
                      onClick={handleOpenTwoFactorSetup}
                      className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                    >
                      {t('security.enable2FAButton')}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      {passwordModal && (
        <Modal onClose={() => { setPasswordModal(false); resetPasswordForm(); }}>
          <div className="flex items-center gap-2 mb-5">
            <Lock className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold text-foreground">{t('account.password_modal_title')}</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">{t('account.current_password')}</label>
              <PasswordInput value={currentPassword} onChange={setCurrentPassword} show={showCurrentPassword} onToggle={() => setShowCurrentPassword(!showCurrentPassword)} />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">{t('account.new_password')}</label>
              <PasswordInput value={newPassword} onChange={setNewPassword} show={showNewPassword} onToggle={() => setShowNewPassword(!showNewPassword)} />
              {newPassword && (
                <div className="mt-2 space-y-1">
                  {passwordRequirements.map((req, i) => (
                    <div key={i} className={`flex items-center gap-1.5 text-xs ${req.check(newPassword) ? 'text-green-500' : 'text-muted-foreground'}`}>
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      <span>{req.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">{t('account.confirm_password')}</label>
              <PasswordInput value={confirmPassword} onChange={setConfirmPassword} show={showConfirmPassword} onToggle={() => setShowConfirmPassword(!showConfirmPassword)} />
              {confirmPassword && !passwordsMatch && (
                <p className="text-xs text-destructive mt-1">{t('account.passwords_no_match')}</p>
              )}
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={() => { setPasswordModal(false); resetPasswordForm(); }}
                className="flex-1 rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted transition-colors"
              >
                {t('account.password_cancel')}
              </button>
              <button
                onClick={handleChangePassword}
                disabled={!canSubmitPassword}
                className="flex-1 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {passwordLoading ? t('account.password_changing') : t('account.password_submit')}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {twoFactorSetupOpen && (
        <ModalShell title={t('security.setup2FAModalTitle')} onClose={() => setTwoFactorSetupOpen(false)}>
          {twoFactorSetupLoading || !twoFactorSetupData ? (
            <div className="space-y-3 animate-pulse">
              <div className="h-40 rounded-xl bg-muted" />
              <div className="h-4 w-full rounded bg-muted" />
              <div className="h-4 w-4/5 rounded bg-muted" />
            </div>
          ) : (
            <div className="space-y-5">
              <div className="grid gap-5 lg:grid-cols-[220px,1fr]">
                <div className="rounded-lg border border-border bg-muted/40 p-4 flex items-center justify-center">
                  <img src={twoFactorSetupData.qrCode} alt={t('security.qrCodeAlt')} className="w-full max-w-[180px]" />
                </div>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">{t('security.scanQRCode')}</h4>
                    <p className="mt-1 text-sm text-muted-foreground">{t('security.scanQRCodeDesc')}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">{t('security.orEnterManually')}</h4>
                    <div className="mt-2 flex flex-col gap-2 rounded-xl border border-border bg-muted/40 p-3 lg:flex-row lg:items-center lg:justify-between">
                      <code className="text-xs text-foreground break-all">{twoFactorSetupData.secret}</code>
                      <button
                        onClick={() => void copyText(twoFactorSetupData.secret)}
                        className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-sm text-foreground hover:bg-background transition-colors"
                      >
                        <Copy className="h-4 w-4" />
                        <span>{t('common:copy')}</span>
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-foreground">{t('security.enterVerificationCode')}</label>
                    <p className="mt-1 text-sm text-muted-foreground">{t('security.enterVerificationCodeDesc')}</p>
                    <input
                      type="text"
                      value={twoFactorVerificationCode}
                      onChange={(e) => setTwoFactorVerificationCode(e.target.value.replace(/\s/g, '').toUpperCase())}
                      placeholder={t('security.verificationCodePlaceholder')}
                      className="input mt-3"
                      autoFocus
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setTwoFactorSetupOpen(false)}
                  className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted transition-colors"
                >
                  {t('subscription.confirm_cancel')}
                </button>
                <button
                  onClick={handleEnableTwoFactor}
                  disabled={twoFactorEnableLoading}
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {twoFactorEnableLoading ? t('security.verifying') : t('security.enable2FAButton')}
                </button>
              </div>
            </div>
          )}
        </ModalShell>
      )}

      {twoFactorDisableOpen && (
        <ModalShell title={t('security.disable2FAModalTitle')} onClose={() => {
          setTwoFactorDisableOpen(false);
          setTwoFactorPassword('');
          setTwoFactorDisableCode('');
        }}>
          <div className="space-y-5">
            <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 text-sm text-amber-700 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
              <span>{t('security.disable2FAWarning')}</span>
            </div>

            <div className="space-y-3">
              <input
                type="password"
                value={twoFactorPassword}
                onChange={(e) => setTwoFactorPassword(e.target.value)}
                placeholder={t('security.enterYourPassword')}
                className="input"
                autoFocus
              />
              <input
                type="text"
                value={twoFactorDisableCode}
                onChange={(e) => setTwoFactorDisableCode(e.target.value.replace(/\s/g, '').toUpperCase())}
                placeholder={t('security.twoFACode')}
                className="input"
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setTwoFactorDisableOpen(false);
                  setTwoFactorPassword('');
                  setTwoFactorDisableCode('');
                }}
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted transition-colors"
              >
                {t('subscription.confirm_cancel')}
              </button>
              <button
                onClick={handleDisableTwoFactor}
                disabled={twoFactorDisableLoading}
                className="rounded-lg bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:opacity-90 transition-colors disabled:opacity-50"
              >
                {twoFactorDisableLoading ? t('security.disabling') : t('security.disable2FAButton')}
              </button>
            </div>
          </div>
        </ModalShell>
      )}

      {twoFactorBackupCodesOpen && (
        <ModalShell title={t('security.backupCodesModalTitle')} onClose={() => setTwoFactorBackupCodesOpen(false)}>
          <div className="space-y-5">
            <p className="text-sm text-muted-foreground">{t('security.saveBackupCodesInfo')}</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {twoFactorBackupCodes.map((code) => (
                <div key={code} className="rounded-xl border border-border bg-muted/40 px-4 py-3 text-center font-mono text-sm text-foreground">
                  {code}
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => void copyText(twoFactorBackupCodes.join('\n'))}
                className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted transition-colors"
              >
                <Copy className="h-4 w-4" />
                <span>{t('security.copyAllCodes')}</span>
              </button>
            </div>
          </div>
        </ModalShell>
      )}

      {topUpOpen && (
        <TopUpModalLite
          onClose={() => setTopUpOpen(false)}
          onSuccess={async () => { await refreshUser(); }}
          t={(key, opts) => t(key as any, opts as any) as string}
        />
      )}
    </>
  );
}

function Modal({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-md mx-4 rounded-lg border border-border bg-card p-6 shadow-lg animate-in fade-in zoom-in-95 duration-200">
        <button onClick={onClose} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground">
          <X className="h-4 w-4" />
        </button>
        {children}
      </div>
    </div>
  );
}

function ModalShell({ title, children, onClose }: { title: string; children: ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-3xl rounded-lg border border-border bg-card p-6 shadow-lg animate-in fade-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute right-4 top-4 text-muted-foreground hover:text-foreground transition-colors">
          <X className="h-4 w-4" />
        </button>
        <div className="mb-5 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
            <KeyRound className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">{title}</h3>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

function PasswordInput({ value, onChange, show, onToggle }: {
  value: string; onChange: (v: string) => void; show: boolean; onToggle: () => void;
}) {
  return (
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full rounded-lg border border-border bg-background px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
      />
      <button type="button" onClick={onToggle} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}
